import { errorLog } from '../core/utils/logger';
import { SigningAdapter, type SignL1ActionParams } from './base-adapter';
import { EIP712_DOMAINS, EIP712_TYPES } from '../core/constants';
import type { SDKContextValue } from '../context/sdk-provider';

export interface PrivyContextAdapterConfig {
  /** SDK context value from React context */
  context: SDKContextValue;
  /** Wallet address to use for signing */
  walletAddress: string;
  /** Show wallet UIs (default: false for headless) */
  showWalletUIs?: boolean;
}

/**
 * Signing adapter that uses Privy's signTypedData from React context
 * Supports both web and React Native environments
 */
export class PrivyContextAdapter extends SigningAdapter {
  private config: PrivyContextAdapterConfig;

  constructor(config: PrivyContextAdapterConfig) {
    super();
    this.config = config;
  }

  async signL1Action(params: SignL1ActionParams): Promise<`0x${string}`> {
    // Runtime check: Ensure Privy iframe is ready before attempting to sign
    // This prevents "iframe not initialized" errors after idle periods
    if (this.config.context.ready === false) {
      errorLog('[Privy Context Adapter] Ready check failed - ready is false');
      throw new Error(
        'Privy not ready. Please wait a moment and try again.'
      );
    }

    if (!this.config.context.signTypedData) {
      errorLog('[Privy Context Adapter] signTypedData function not available');
      throw new Error(
        'signTypedData not available in SDK context. Make sure your app is wrapped with HyperliquidSDKProvider.'
      );
    }

    const eip712Message = {
      domain: EIP712_DOMAINS.EXCHANGE,
      primaryType: 'Agent',
      types: {
        EIP712Domain: [
          { name: 'name', type: 'string' },
          { name: 'version', type: 'string' },
          { name: 'chainId', type: 'uint256' },
          { name: 'verifyingContract', type: 'address' },
        ],
        ...EIP712_TYPES.AGENT,
      },
      message: {
        source: params.isTestnet ? 'b' : 'a',
        connectionId: params.actionHash,
      },
    };

    const signOptions = {
      uiOptions: { showWalletUIs: this.config.showWalletUIs ?? false },
      address: this.config.walletAddress,
    };

    try {
      const { signature } = await this.config.context.signTypedData(
        eip712Message,
        signOptions
      );

      return signature as `0x${string}`;
    } catch (error) {
      errorLog('[Privy Context Adapter] === SIGNING FAILED ===');
      errorLog('[Privy Context Adapter] Error:', error);
      errorLog('[Privy Context Adapter] Error type:', (error as Error)?.constructor?.name);
      errorLog('[Privy Context Adapter] Error message:', (error as Error)?.message);
      errorLog('[Privy Context Adapter] Ready state at error:', this.config.context.ready);
      throw error; // Re-throw original error
    }
  }

  async isReady(): Promise<boolean> {
    const readyState = this.config.context.ready;
    const hasSignFn = !!this.config.context.signTypedData;
    const hasWallet = !!this.config.walletAddress;
    return !!(readyState !== false && hasSignFn && hasWallet);
  }

  getWalletAddress(): string {
    return this.config.walletAddress;
  }
}
